<script setup lang="ts">
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core"
import { TuInputLabeledSwitch } from "@tinyurldev/tu-ui-vue"

import type { BillingPeriod } from "@Modules/subscriptions/Api/SubscriptionsApi"

//
// Setup
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5)

//
// Props
interface Props {
  billingPeriod: BillingPeriod
}
defineProps<Props>()

//
// Emit
const emit = defineEmits<{
  (e: "on-billing-period-update", value: BillingPeriod): void
}>()

//
// Computed
const isMobileView = smallerOrEqual("xl")

//
// Methods
function handleBillingPeriodChange(value: string): void {
  emit("on-billing-period-update", value as BillingPeriod)
}
</script>

<template>
  <div
    class="org-billing-frequency"
    :class="{ 'org-billing-frequency--mobile': isMobileView }"
  >
    <TuInputLabeledSwitch
      :value="billingPeriod"
      class="org-billing-frequency__toggler _toggler"
      true-label="Annually"
      true-value="yearly"
      false-label="Monthly"
      false-value="monthly"
      @input="handleBillingPeriodChange"
    />

    <p class="org-billing-frequency__tax-disclaimer _tax_disclaimer">
      * Listed prices exclude any applicable taxes.
    </p>

    <RouterLink
      class="org-billing-frequency__cta _view_features"
      :to="{ name: 'signup.pricing' }"
    >
      View All Features
    </RouterLink>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-billing-frequency {
  $self: &;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  max-width: 940px;

  &__tax-disclaimer {
    display: none;

    color: color.$text-secondary;
    font-size: typography.$xxs-font-size;
    font-style: italic;
  }

  &__cta {
    font-weight: typography.$font-weight-bold;
  }

  &--mobile {
    flex-direction: column;
    align-items: flex-start;

    #{$self}__toggler {
      margin-bottom: spacing.$xxs;
    }

    #{$self}__tax-disclaimer {
      display: block;
      margin-bottom: spacing.$xxs;
    }

    #{$self}__cta {
      align-self: flex-end;
    }
  }
}
</style>
